import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

export interface SignatureCanvasHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toPngBytes: () => Uint8Array | null;
}

interface Props {
  className?: string;
  ariaLabel?: string;
}

/**
 * Mouse + touch + stylus signature pad. Renders into a high-DPI canvas and
 * exposes imperative helpers to clear, check emptiness, and export a PNG.
 *
 * The rendered PNG preserves transparency so it composites cleanly onto the
 * lease PDF in pdf-lib.
 */
export const SignatureCanvas = forwardRef<SignatureCanvasHandle, Props>(
  function SignatureCanvas({ className, ariaLabel }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const lastPointRef = useRef<{ x: number; y: number } | null>(null);
    const dirtyRef = useRef(false);
    const [, force] = useState(0);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0a1430";
    }, []);

    useImperativeHandle(ref, () => ({
      clear() {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        dirtyRef.current = false;
        force((n) => n + 1);
      },
      isEmpty() {
        return !dirtyRef.current;
      },
      toPngBytes() {
        const canvas = canvasRef.current;
        if (!canvas || !dirtyRef.current) return null;
        const dataUrl = canvas.toDataURL("image/png");
        const b64 = dataUrl.split(",")[1] ?? "";
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
      },
    }));

    function pointFromEvent(e: PointerEvent | React.PointerEvent): {
      x: number;
      y: number;
    } | null {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    function startDraw(e: React.PointerEvent) {
      e.preventDefault();
      drawingRef.current = true;
      lastPointRef.current = pointFromEvent(e);
      (e.target as Element).setPointerCapture(e.pointerId);
    }

    function moveDraw(e: React.PointerEvent) {
      if (!drawingRef.current) return;
      const next = pointFromEvent(e);
      const prev = lastPointRef.current;
      if (!next || !prev) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!ctx) return;
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(next.x, next.y);
      ctx.stroke();
      lastPointRef.current = next;
      dirtyRef.current = true;
    }

    function endDraw(e: React.PointerEvent) {
      drawingRef.current = false;
      lastPointRef.current = null;
      try {
        (e.target as Element).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      force((n) => n + 1);
    }

    return (
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={ariaLabel ?? "Signature pad"}
        className={
          className ??
          "block w-full h-44 rounded-lg border border-slate-300 bg-white touch-none cursor-crosshair"
        }
        onPointerDown={startDraw}
        onPointerMove={moveDraw}
        onPointerUp={endDraw}
        onPointerCancel={endDraw}
        onPointerLeave={endDraw}
      />
    );
  },
);
